import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import { RequestStatus } from '../../../../enums/RequestStatusEnum';

const CreditNoteModal = ({ open, onClose, invoiceNumber, amount, fetchInvoices }) => {
  const [loading, setLoading] = useState(false);
  const [fetchedAmount, setFetchedAmount] = useState(amount);
  const [description, setDescription] = useState('');
  const [adminDescription, setAdminDescription] = useState('');
  const [error, setError] = useState(null);
  const [isEditInvoiceCreated, setIsEditInvoiceCreated] = useState(false);
  const [isRequestApproved, setIsRequestApproved] = useState(false);
  const [amountLabel, setAmountLabel] = useState('Enter amount to create credit note');
  const [isInvoiceEditItemsEmpty, setIsInvoiceEditItemsEmpty] = useState(false);

  useEffect(() => {
    if (open) {
      fetchEditItems();
    }
  }, [open]);

  const fetchEditItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/AdminInvoiceEdit/${invoiceNumber}?requestType=${RequestStatus.Credit}`);
      const data = response.data;
      setIsEditInvoiceCreated(data?.isAnyResponseOnEditCreditInvoice);
      setIsRequestApproved(data?.isRequestApproved);
      if (data && data.invoiceEditItems) {
        setIsInvoiceEditItemsEmpty(data.invoiceEditItems.length === 0);
        const amountItem = data.invoiceEditItems[0]?.requestedValue;
        const userCommentItem = data.invoiceEditItems[0]?.userComment;

        setFetchedAmount(amountItem || amount);
        setAmountLabel(amountItem ? 'Credit note requested amount' : 'Enter amount to create credit note');

        if (userCommentItem) {
          setDescription(userCommentItem);
        } else {
          setDescription('');
        }
      } else {
        setIsInvoiceEditItemsEmpty(true);
        setAmountLabel('Enter amount to create credit note');
      }
    } catch (err) {
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (e) => {
    setFetchedAmount(e.target.value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const requestBody = {
        FieldRequestedValue: fetchedAmount,
        AdminComment: adminDescription,
        InvoiceNumber: invoiceNumber,
      };
      if (!isInvoiceEditItemsEmpty) {
        requestBody.UserComment = description;
      }
      await axios.post(`/AdminCreditInvoice/${invoiceNumber}`, requestBody);
      toast.success(`Credit note for invoice ${invoiceNumber} accepted successfully!`);
      fetchInvoices();
      onClose();
    } catch (err) {
      setError('Failed to accept credit note. Please try again.');
      toast.error('Failed to accept credit note.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditNoteReject = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.put(`/AdminCreditInvoice`, {
        FieldRequestedValue: fetchedAmount,
        UserComment: description,
        AdminComment: adminDescription,
        InvoiceNumber: invoiceNumber,
      });
      toast.success(`Credit note for invoice ${invoiceNumber} submitted successfully!`);
      onClose();
    } catch (err) {
      setError('Failed to submit credit note. Please try again.');
      toast.error('Failed to submit credit note.');
    } finally {
      setLoading(false);
    }
  };

  // Only enable buttons if both description and adminDescription are filled
  const isActionEnabled = (isInvoiceEditItemsEmpty
    ? adminDescription.trim() !== ''
    : description.trim() !== '' && adminDescription.trim() !== ''
  );

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="credit-note-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 450,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography id="credit-note-modal-title" variant="h6" component="h2">
            Credit Note Details
          </Typography>
          <CloseIcon onClick={onClose} sx={{ cursor: 'pointer', color: '#555' }} />
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" textAlign="center" mb={3}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <>
            <Box mb={3}>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                {amountLabel}
              </Typography>
              <input
                type="number"
                value={fetchedAmount}
                onChange={handleAmountChange}
                disabled
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '1rem',
                  backgroundColor: '#f5f5f5',
                  color: '#888',
                }}
              />
            </Box>
            {!isInvoiceEditItemsEmpty && (
              <Box>
                <Typography variant="body1" fontWeight="bold" mb={1}>
                  Description
                </Typography>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '1rem',
                    resize: 'none',
                    backgroundColor: '#fff',
                  }}
                  rows={4}
                />
              </Box>
            )}
            <Box mt={3}>
              <Typography variant="body1" fontWeight="bold" mb={1}>
                Admin Description
              </Typography>
              <textarea
                value={adminDescription}
                onChange={(e) => setAdminDescription(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '1rem',
                  resize: 'none',
                  backgroundColor: '#fff',
                }}
                rows={3}
              />
            </Box>
          </>
        )}
        <div className={`${isInvoiceEditItemsEmpty ? "d-block" : "d-flex"}`} style={{ justifyContent: 'end', alignItems: 'baseline' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            {!isEditInvoiceCreated && !isInvoiceEditItemsEmpty && (
              <Button
                variant="outlined"
                sx={{
                  width: '45%',
                  fontWeight: 'bold',
                  color: '#d32f2f',
                  borderColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#f8d7da',
                    borderColor: '#d32f2f',
                  },
                }}
                onClick={handleCreditNoteReject}
                disabled={!isActionEnabled || loading}
              >
                Reject
              </Button>
            )}
            {!isEditInvoiceCreated && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{ width: '45%', fontWeight: 'bold' }}
                disabled={!isActionEnabled || loading}
              >
                {isInvoiceEditItemsEmpty ? 'Create Credit Note' : 'Accept'}
              </Button>
            )}
            {isEditInvoiceCreated && (
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: isRequestApproved ? 'green' : 'red',
                  textAlign: 'center',
                }}
              >
                {isRequestApproved ? 'Accepted' : 'Rejected'}
              </Typography>
            )}
          </Box>
        </div>
      </Box>
    </Modal>
  );
};

export default CreditNoteModal;
